/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Present personal sign-in and the entry to organization-managed student authentication.
 * @state-owner React Hook Form; host auth adapter owns credentials
 * @allowed-imports React Hook Form; Zod resolver; app session provider; module schema; shared i18n
 * @forbidden token storage; role selection; API entity cache
 * @public-api workspace shell gate
 * @side-effects host authentication calls
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 * @security-boundary unauthenticated visitor to Supabase Auth
 * @data-classification credentials in transit only
 * @trust-model successful authentication proves identity only
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { useTranslator } from '../../../shared/model/use-workspace';
import { type SignInValues, signInSchema } from '../schemas/sign-in';

export function SignInScreen() {
  const { auth, refresh } = useWorkspaceSession();
  const t = useTranslator();
  const [error, setError] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const submit = async (values: SignInValues) => {
    setError(null);
    try {
      await auth.signInWithPassword(values);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '登入失敗，請再試一次');
    }
  };

  const signInWithGoogle = async () => {
    setError(null);
    setGoogleBusy(true);
    try {
      await auth.signInWithGoogle();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Google 登入失敗');
      setGoogleBusy(false);
    }
  };

  return (
    <main className="entry-screen">
      <div className="joinbox" style={{ width: 'min(420px, 92vw)' }}>
        <div className="jlogo">V</div>
        <h3>登入 Vispo</h3>
        <span>登入後會先確認你的身分，再帶你進入對應的工作區。</span>
        <button
          className="bt"
          disabled={googleBusy || isSubmitting}
          style={{ marginTop: 16, width: '100%' }}
          type="button"
          onClick={() => void signInWithGoogle()}
        >
          {googleBusy ? '正在前往 Google…' : '用 Google 繼續'}
        </button>
        <form onSubmit={handleSubmit(submit)} style={{ marginTop: 14, textAlign: 'left' }}>
          <div className="sec5" style={{ marginTop: 0 }}>
            Email
          </div>
          <input
            autoComplete="email"
            style={{ width: '100%', padding: '9px 12px' }}
            type="email"
            {...register('email')}
          />
          {errors.email ? <p className="jmsg">{errors.email.message}</p> : null}
          <div className="sec5">密碼</div>
          <input
            autoComplete="current-password"
            style={{ width: '100%', padding: '9px 12px' }}
            type="password"
            {...register('password')}
          />
          {errors.password ? <p className="jmsg">{errors.password.message}</p> : null}
          {error ? <p className="jmsg">{error}</p> : null}
          <button
            className="bt pri"
            disabled={isSubmitting}
            style={{ marginTop: 16, width: '100%' }}
            type="submit"
          >
            {isSubmitting ? '登入中…' : '用 Email 登入'}
          </button>
        </form>
        <a
          className="bt"
          href="/student/org-login"
          style={{ display: 'block', marginTop: 12, textAlign: 'center', width: '100%' }}
        >
          {t('已有機構帳號？')}
        </a>
      </div>
    </main>
  );
}
